import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, MapPin, Search } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import type { PropertyListItem } from "./types";

interface PropertySwitcherSearchProps {
  currentPropertyId: string | undefined;
  filteredProperties: PropertyListItem[];
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSelect: (propertyId: string) => void;
}

/** Always-visible search input that switches property; the results panel
    shows the client's full property list until a term narrows it. The panel
    is portaled to document.body (same approach as LivePropertySearch) so it
    can't blend with page content inside the header's stacking context. */
export function PropertySwitcherSearch({
  currentPropertyId,
  filteredProperties,
  searchTerm,
  onSearchTermChange,
  onSelect,
}: PropertySwitcherSearchProps) {
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  };

  useLayoutEffect(() => {
    if (open) {
      updatePanelPosition();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (propertyId: string) => {
    onSelect(propertyId);
    onSearchTermChange("");
    setOpen(false);
  };

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          style={{
            position: "absolute",
            top: panelPosition.top,
            left: panelPosition.left,
            zIndex: 9999,
          }}
          className="w-[400px] max-h-[400px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {filteredProperties.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No properties found
            </div>
          ) : (
            filteredProperties.map((prop) => (
              <button
                key={prop.id}
                onClick={() => handleSelect(prop.id)}
                className={cn(
                  "w-full cursor-pointer p-3 text-left hover:bg-accent transition-colors",
                  prop.id === currentPropertyId && "bg-accent"
                )}
              >
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{prop.name}</span>
                    {prop.id === currentPropertyId && (
                      <Badge variant="outline" className="ml-auto text-xs">Current</Badge>
                    )}
                  </div>
                  {prop.address && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground ml-6">
                      <MapPin className="h-3 w-3" />
                      <span>{prop.address}</span>
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} className="relative w-72 flex-shrink-0">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        placeholder="Search by name or address..."
        value={searchTerm}
        onChange={(e) => {
          onSearchTermChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        className="pl-9 h-9"
      />
      {panel}
    </div>
  );
}
